import React from 'react'
import { Modal, Box, Text, Flex, Timeline, Label } from '@workwhile/ui'
import { Work } from 'api/typings'
import { formatInTimeZone } from 'date-fns-tz'
import { differenceInMinutes, isAfter } from 'date-fns'
import { getLongDuration } from 'lib/time'
import { ShiftStatusRender } from 'pages/UnpaidShifts/components/UnpaidShiftsRenderers/ShiftStatusRender'
import { useShiftByIdQuery } from 'queries/shifts/useShiftByIdQuery'

interface TimelineTripModalProps {
  open: boolean
  onClose: () => void
  work: Work
}

export const TimelineTripModal: React.FC<TimelineTripModalProps> = ({
  open,
  onClose,
  work,
}) => {
  const { data: shift } = useShiftByIdQuery(work.shift?.id ?? 0)

  if (!shift) {
    return null
  }

  const timezone = shift.location.address.timezone

  const shiftStart = shift.startsAt ? new Date(shift.startsAt) : null
  const shiftEnd = shift.endsAt ? new Date(shift.endsAt) : null
  const workerStart = work.startedAt ? new Date(work.startedAt) : null
  const workerEnd = work.completedAt ? new Date(work.completedAt) : null

  const timelineItems: Array<{
    label: string
    timestamp: Date
    badge?: { text: string; variant: 'success' | 'error' }
  }> = []

  // Shift Start
  if (shiftStart) {
    timelineItems.push({
      label: 'Shift Start',
      timestamp: shiftStart,
    })
  }

  // Worker Start
  if (workerStart) {
    timelineItems.push({
      label: 'Worker Start',
      timestamp: workerStart,
    })
  }

  // Worker End
  if (workerEnd) {
    let badge: { text: string; variant: 'success' | 'error' } | undefined
    if (shiftEnd) {
      if (shiftEnd) {
        const diffMinutes = Math.abs(differenceInMinutes(shiftEnd, workerEnd))
        // Only show badge if there's a meaningful difference (more than 1 minute)
        if (diffMinutes > 1) {
          const clockOutEarly = isAfter(shiftEnd, workerEnd)
          badge = {
            text: `${getLongDuration(diffMinutes)}${clockOutEarly ? ' early' : ' late'}`,
            variant: clockOutEarly ? 'error' : 'success',
          }
        }
      }
    }
    timelineItems.push({
      label: 'Worker End',
      timestamp: workerEnd,
      badge,
    })
  }

  // Shift End
  if (shiftEnd) {
    timelineItems.push({
      label: 'Shift End',
      timestamp: shiftEnd,
    })
  }

  // Sort by timestamp
  timelineItems.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

  const firstTripEmbedUrl =
    work.trips && work.trips.length > 0 && work.trips[0]?.embedUrl
      ? work.trips[0].embedUrl
      : null

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Timeline & Trip Details"
      // @ts-expect-error width is not typed
      width="95vw"
      height="95vh"
      maxWidth="1600px"
    >
      <Flex gap={2} alignItems="flex-start">
        {/* Timeline Section */}
        <Box m={2}>
          <Text as="span" fontWeight="bold" mb={2} display="block">
            Shift duration:
          </Text>
          <Text fontSize={2} mb={3}>
            {getLongDuration(
              differenceInMinutes(
                shiftEnd ?? new Date(),
                shiftStart ?? new Date()
              )
            )}
          </Text>
          <Timeline>
            {timelineItems.map((item, index) => {
              const formattedTime = formatInTimeZone(
                item.timestamp,
                timezone,
                'MMM d, yyyy, h:mm a z'
              )

              return (
                <Timeline.Item
                  key={`${item.label}-${item.timestamp.getTime()}-${index}`}
                >
                  <Flex alignItems="center" gap={1} flexWrap="wrap">
                    <Text color="lightText" fontSize={0}>
                      {formattedTime}
                    </Text>
                    {item.badge ? (
                      <Label variant={item.badge.variant} inline size="small">
                        {item.badge.text}
                      </Label>
                    ) : null}
                  </Flex>
                  <Text fontSize={2}>{item.label}</Text>
                </Timeline.Item>
              )
            })}
          </Timeline>
          <Text fontSize={2} mb={2} mt={3}>
            <Text as="span" fontWeight="bold">
              Work Status:
            </Text>{' '}
            <ShiftStatusRender value={work.status || ''} />
          </Text>
          <Text fontWeight="bold" mb={1}>
            Early Clockout Reason:
          </Text>
          <Text fontSize={2} mb={3}>
            {work.reasonEarlyClockout || 'N/A'}
          </Text>
        </Box>

        {/* Trip Iframe Section */}
        {firstTripEmbedUrl ? (
          <Box flex={1} minWidth={0}>
            <Text fontWeight="bold" mb={2}>
              Hypertrack data
            </Text>
            <Box
              width="100%"
              height="80vh"
              minHeight="800px"
              border="1px solid"
              borderColor="border"
              borderRadius={1}
              overflow="hidden"
            >
              <iframe
                src={firstTripEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                title="Trip Location"
              />
            </Box>
          </Box>
        ) : (
          <Box flex={1}>
            <Text color="lightText">No trip data available</Text>
          </Box>
        )}
      </Flex>
    </Modal>
  )
}
